import { useState, useMemo, useEffect } from 'react';
import { ColorSwatch } from '../atoms';
import { ColorPalette } from './ColorPalette';
import { RECOMMENDED_COLORS } from '../../constants';
import { isValidHexColor, normalizeHexColor } from '../../utils';
import { Input } from '@/modules/Input';
import { Button } from '@/modules/Button';
import { useEyeDropper } from '../../hooks';
import { cn } from '@/shared/utils/cn';
import { faPalette } from '@fortawesome/pro-regular-svg-icons';
import { faEyeDropper } from '@fortawesome/pro-solid-svg-icons';

interface ColorPickerContentProps {
  /** Color actual seleccionado */
  currentColor?: string;
  /** Colores recientes */
  recentColors: string[];
  /** Callback cuando se selecciona un color */
  onColorSelect: (color: string) => void;
  /** Si mostrar el input hexadecimal */
  showHexInput?: boolean;
  /** Clase personalizada */
  className?: string;
}

/**
 * Componente molecule que contiene el contenido del picker de color
 */
export const ColorPickerContent = ({
  currentColor = '',
  recentColors,
  onColorSelect,
  showHexInput = true,
  className,
}: ColorPickerContentProps) => {
  const [hexInput, setHexInput] = useState(currentColor || '');
  const [inputError, setInputError] = useState('');
  const { isSupported, isActive, pickColor } = useEyeDropper();

  // Color normalizado para usar en el selector
  const normalizedColor = useMemo(() => {
    if (!currentColor) return '#000000';
    const normalized = normalizeHexColor(currentColor);
    return isValidHexColor(normalized) ? normalized : '#000000';
  }, [currentColor]);

  // Colores a mostrar (recientes o recomendados)
  const colorsToShow = useMemo(() => {
    if (recentColors.length > 0) {
      return recentColors;
    }
    return RECOMMENDED_COLORS;
  }, [recentColors]);

  // Manejar cambio en el input hexadecimal
  const handleHexInputChange = (value: string) => {
    setHexInput(value);
    setInputError('');

    // Si el color es válido, aplicarlo
    const normalized = normalizeHexColor(value);
    if (isValidHexColor(normalized)) {
      onColorSelect(normalized);
    }
  };

  // Manejar submit del input hexadecimal
  const handleHexInputSubmit = () => {
    const normalized = normalizeHexColor(hexInput);
    if (isValidHexColor(normalized)) {
      onColorSelect(normalized);
      setInputError('');
    } else {
      setInputError('Color hexadecimal inválido');
    }
  };

  // Manejar selección de color desde swatch (click directo, guardar inmediatamente)
  const handleSwatchClick = (color: string) => {
    onColorSelect(color);
    setHexInput(color);
    setInputError('');
  };

  // Manejar cambio desde el selector de colores
  const handlePaletteChange = (color: string) => {
    onColorSelect(color);
    setHexInput(color);
    setInputError('');
  };

  // Manejar click en el botón de eyedropper
  const handleEyeDropperClick = async () => {
    const color = await pickColor();
    if (color) {
      const normalized = normalizeHexColor(color);
      if (isValidHexColor(normalized)) {
        onColorSelect(normalized);
        setHexInput(normalized);
        setInputError('');
      }
    }
  };

  // Sincronizar input cuando cambia el color actual
  useEffect(() => {
    if (currentColor && currentColor !== hexInput) {
      setHexInput(currentColor);
    }
  }, [currentColor, hexInput]);

  return (
    <div className={cn('ui-flex ui-min-w-[340px] ui-flex-col ui-gap-16 ui-p-16', className)}>
      {/* Selector de colores completo */}
      <ColorPalette color={normalizedColor} onColorChange={handlePaletteChange} />

      {/* Input hexadecimal con botón de eyedropper */}
      {showHexInput && (
        <div className="ui-flex ui-flex-col ui-gap-2">
          <div className="ui-flex ui-items-end ui-gap-2">
            <div className="ui-flex-1">
              <Input
                label="Color hexadecimal"
                value={hexInput}
                onValueChange={handleHexInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleHexInputSubmit();
                  }
                }}
                error={inputError}
                placeholder="#000000"
                leftSlot={faPalette}
                className="ui-w-full"
              />
            </div>
            {isSupported && (
              <Button
                icon={faEyeDropper}
                size="sm"
                color="tertiary"
                theme="solid"
                onClick={handleEyeDropperClick}
                disabled={isActive}
                tooltip={isActive ? 'Seleccionando color...' : 'Seleccionar color de la pantalla'}
                tooltipPosition="top"
                className="ui-shrink-0"
              />
            )}
          </div>
        </div>
      )}

      {/* Colores recientes/recomendados */}
      <div className="ui-flex ui-flex-col ui-gap-2">
        <div className="ui-flex ui-items-center ui-justify-between">
          <span className="ui-text-body-sm-regular ui-font-body-sm-regular ui-text-tertiary">
            {recentColors.length > 0 ? 'Colores recientes' : 'Colores recomendados'}
          </span>
        </div>
        <div className="ui-flex ui-flex-wrap ui-gap-2">
          {colorsToShow.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={normalizedColor?.toUpperCase() === color.toUpperCase()}
              onClick={() => handleSwatchClick(color)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
